import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Stack,
  IconButton,
  Tooltip,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  useTheme,
  Collapse,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  AutoAwesome as AutoIcon,
  ArrowBack,
  ArrowForward,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
  LocationOn,
  CalendarToday,
  PhotoCamera,
  Add as AddIcon,
  Close as CloseIcon,
  CheckCircle,
  Edit as EditIcon,
  SkipNext as SkipIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MenuBook as PagesIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Star as StarIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import AuthenticatedImage from '../AuthenticatedImage';
import ImageAnalysisDetailsDialog from '../ImageAnalysisDetailsDialog';
import albumService from '../../api/albumService';
import sessionService from '../../api/sessionService';

// ── Constants ──────────────────────────────────────────────────────────────────

const SCENE_TYPE_COLOR = {
  day: 'primary',
  location: 'success',
  phase: 'warning',
  moment: 'secondary',
  ceremony: 'error',
};

const SCENE_TYPE_LABEL = {
  day: 'Day',
  location: 'Location',
  phase: 'Phase',
  moment: 'Moment',
  ceremony: 'Ceremony',
};

const ORG_LABEL = {
  days: 'Organized by days',
  places: 'Organized by places',
  phases: 'Organized by phases',
  moments: 'Organized by moments',
};

const sanitizeStorySettings = (settings = {}) => ({
  userContext: String(settings.userContext || ''),
  albumTopic: String(settings.albumTopic || '').trim(),
  numberOfPages: Math.max(1, parseInt(settings.numberOfPages, 10) || 1),
  repImages: Math.max(1, parseInt(settings.repImages, 10) || 1),
  topImagesCount: Math.max(1, parseInt(settings.topImagesCount, 10) || 1),
  temperature: Math.max(0, Math.min(1, parseFloat(settings.temperature) || 0.6)),
});

const makeSettingsSignature = (settings = {}) => JSON.stringify(sanitizeStorySettings(settings));

const computeDefaultStorySettings = (data) => {
  const clusters = (data?.clusters || []).filter(c => c.id !== 'unclustered');
  const clusterCount = clusters.length;
  const oneThirdClusters = Math.max(1, Math.round(clusterCount / 3));

  const largestClusterSize = clusters.reduce((maxSize, cluster) => {
    if (Array.isArray(cluster.imageIds)) {
      return Math.max(maxSize, cluster.imageIds.length);
    }
    const fallbackCount = (data?.images || []).filter(img => String(img.clusterId) === String(cluster.id)).length;
    return Math.max(maxSize, fallbackCount);
  }, 0);

  return {
    numberOfPages: oneThirdClusters,
    topImagesCount: Math.max(1, Math.round(clusterCount * 0.2)),
    repImages: Math.max(1, largestClusterSize),
  };
};

// ── Scene card ──────────────────────────────────────────────────────────────────

const SceneCard = React.memo(({ scene, sceneIndex, allImages, onEditImages, included, onToggleInclude, sceneRationale }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [imageDetailDialog, setImageDetailDialog] = useState({ open: false, imageId: null });

  // Highlight picks (suggestedImageIds auto-filled by backend)
  const suggestedImages = useMemo(
    () =>
      (scene.imageIds || scene.suggestedImageIds || [])
        .map(id => allImages.find(img => img.id === id))
        .filter(Boolean),
    [scene, allImages]
  );

  // ALL images that belong to any cluster assigned to this scene, grouped by cluster
  const clusterGroups = useMemo(() => {
    if (!scene.suggestedClusterIds?.length) return [];
    return scene.suggestedClusterIds.map(cid => ({
      clusterId: cid,
      images: allImages.filter(img => String(img.clusterId) === String(cid)),
      label: allImages.find(img => String(img.clusterId) === String(cid))?.clusterLabel || cid,
    })).filter(g => g.images.length > 0);
  }, [scene.suggestedClusterIds, allImages]);

  const typeColor = SCENE_TYPE_COLOR[scene.sceneType] || 'default';

  return (
    <>
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        gap: 0,
        overflow: 'hidden',
        transition: 'box-shadow 0.15s',
        '&:hover': { boxShadow: 3 },
      }}
    >
      {/* Left colour strip */}
      <Box
        sx={{
          width: 6,
          flexShrink: 0,
          bgcolor: `${typeColor}.main`,
          opacity: 0.85,
        }}
      />

      <Box sx={{ flex: 1, p: 2 }}>
        {/* Header row */}
        <Stack direction="row" alignItems="flex-start" spacing={1} flexWrap="wrap">
          <Typography variant="h3" component="span" sx={{ fontSize: '1.6rem', lineHeight: 1, mr: 0.5 }}>
            {scene.emoji || '📷'}
          </Typography>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <Typography variant="subtitle1" fontWeight={700}>
                {sceneIndex + 1}. {scene.sceneName}
              </Typography>
              <Chip
                label={SCENE_TYPE_LABEL[scene.sceneType] || scene.sceneType || 'Scene'}
                color={typeColor}
                size="small"
                variant="outlined"
              />
              {scene.themeRelevanceScore != null && (
                <Tooltip title={sceneRationale || ''} placement="top" disableHoverListener={!sceneRationale}>
                  <Chip
                    label={`★ ${scene.themeRelevanceScore}/10`}
                    size="small"
                    color={scene.themeRelevanceScore >= 8 ? 'success' : scene.themeRelevanceScore >= 5 ? 'warning' : 'error'}
                    variant="outlined"
                    title={sceneRationale ? undefined : 'Theme relevance score'}
                  />
                </Tooltip>
              )}
              {scene.dateHint && (
                <Chip
                  icon={<CalendarToday sx={{ fontSize: 12 }} />}
                  label={scene.dateHint}
                  size="small"
                  variant="outlined"
                  color="default"
                />
              )}
              {/* Cluster ID chips */}
              {(scene.suggestedClusterIds || []).map(cid => (
                <Chip
                  key={cid}
                  label={cid.replace('cluster_', '#')}
                  size="small"
                  variant="outlined"
                  sx={{ fontFamily: 'monospace', fontSize: '0.65rem', borderColor: 'divider', color: 'text.secondary' }}
                />
              ))}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {scene.sceneDescription}
            </Typography>
          </Box>
        </Stack>

        {/* Image strips */}
        {/* ── Highlight picks (top 3 auto-selected) ── */}
        {suggestedImages.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Highlights
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {suggestedImages.map(img => (
                <Box
                  key={img.id}
                  onClick={() => setImageDetailDialog({ open: true, imageId: img.id })}
                  sx={{
                    width: 72, height: 56, borderRadius: 1, overflow: 'hidden',
                    border: '1px solid', borderColor: 'divider', bgcolor: 'grey.100', flexShrink: 0,
                    cursor: 'pointer', '&:hover': { opacity: 0.8, outline: '2px solid', outlineColor: 'primary.main' },
                  }}
                >
                  <AuthenticatedImage
                    imageId={img.id} type="thumbnail" alt={img.name}
                    sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ── Clusters: one collapsible row per cluster ── */}
        {clusterGroups.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {clusterGroups.length} cluster{clusterGroups.length !== 1 ? 's' : ''} &middot;{' '}
                {clusterGroups.reduce((s, g) => s + g.images.length, 0)} photos
              </Typography>
              <IconButton size="small" onClick={() => setExpanded(v => !v)} sx={{ p: 0.25 }}>
                {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Stack>
            <Collapse in={expanded} timeout="auto">
              <Stack spacing={1} sx={{ mt: 0.5 }}>
                {clusterGroups.map(group => (
                  <Box
                    key={group.clusterId}
                    sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 0.75 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                      <Chip
                        label={group.clusterId.replace('cluster_', '#')}
                        size="small"
                        sx={{ fontFamily: 'monospace', fontSize: '0.65rem', height: 18 }}
                      />
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
                        {group.label}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {group.images.length} photos
                      </Typography>
                    </Stack>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {group.images.map(img => (
                        <Tooltip key={img.id} title={img.name || img.originalName || ''} placement="top">
                          <Box
                            onClick={() => setImageDetailDialog({ open: true, imageId: img.id })}
                            sx={{
                              width: 60, height: 48, borderRadius: 0.75, overflow: 'hidden',
                              border: '1px solid', borderColor: 'divider', bgcolor: 'grey.100', flexShrink: 0,
                              cursor: 'pointer', '&:hover': { opacity: 0.8, outline: '2px solid', outlineColor: 'primary.main' },
                            }}
                          >
                            <AuthenticatedImage
                              imageId={img.id} type="thumbnail" alt={img.name}
                              sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                            />
                          </Box>
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}

        {/* Edit button row */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            {suggestedImages.length} photo{suggestedImages.length !== 1 ? 's' : ''} in this scene
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon sx={{ fontSize: 14 }} />}
            onClick={() => onEditImages(sceneIndex)}
            sx={{ py: 0.25, fontSize: '0.7rem' }}
          >
            Edit photos
          </Button>
          <Tooltip title={included === false ? 'Excluded from album — click to include' : 'Included in album — click to exclude'}>
            <IconButton
              size="small"
              onClick={() => onToggleInclude(scene.sceneId)}
              sx={{ ml: 'auto', color: included === false ? 'action.disabled' : 'success.main' }}
            >
              {included === false
                ? <UncheckedIcon sx={{ fontSize: 20 }} />
                : <CheckCircle sx={{ fontSize: 20 }} />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Paper>
    <ImageAnalysisDetailsDialog
      open={imageDetailDialog.open}
      onClose={() => setImageDetailDialog({ open: false, imageId: null })}
      imageId={imageDetailDialog.imageId}
    />
  </>
  );
});

// ── Image picker dialog ────────────────────────────────────────────────────────

const ImagePickerDialog = ({ open, sceneIndex, sceneName, scenes, setScenes, allImages, onClose }) => {
  const scene = scenes[sceneIndex];
  const currentIds = useMemo(
    () => new Set(scene?.imageIds || scene?.suggestedImageIds || []),
    [scene]
  );
  // Local copy during editing
  const [selectedIds, setSelectedIds] = useState(() => new Set(currentIds));

  useEffect(() => {
    if (open) setSelectedIds(new Set(scene?.imageIds || scene?.suggestedImageIds || []));
  }, [open, scene]);

  const toggle = useCallback(id => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSave = () => {
    setScenes(prev =>
      prev.map((s, i) =>
        i === sceneIndex ? { ...s, imageIds: Array.from(selectedIds) } : s
      )
    );
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <EditIcon />
          <Typography variant="h6" sx={{ flex: 1 }}>
            Edit photos – {sceneName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {selectedIds.size} selected
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {allImages.map(img => {
            const selected = selectedIds.has(img.id);
            return (
              <Tooltip key={img.id} title={img.name} placement="top">
                <Box
                  onClick={() => toggle(img.id)}
                  sx={{
                    width: 80,
                    height: 64,
                    borderRadius: 1,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: selected ? '3px solid' : '2px solid transparent',
                    borderColor: selected ? 'success.main' : 'transparent',
                    boxShadow: selected ? 3 : 1,
                    transition: 'all 0.12s',
                    position: 'relative',
                    '&:hover': { transform: 'scale(1.06)', boxShadow: 4 },
                    bgcolor: 'grey.100',
                  }}
                >
                  <AuthenticatedImage
                    imageId={img.id}
                    type="thumbnail"
                    alt={img.name}
                    sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                  />
                  {selected && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 2,
                        right: 2,
                        bgcolor: 'success.main',
                        borderRadius: '50%',
                        width: 18,
                        height: 18,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CheckCircle sx={{ fontSize: 14, color: '#fff' }} />
                    </Box>
                  )}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save ({selectedIds.size} photos)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

/**
 * WizardStep5Story – Story / Timeline
 * Generates a narrative timeline for the album using cluster summaries + unclustered images.
 * Props: session, onNext, onBack
 */
const WizardStep5Story = ({ session, onNext, onBack, onRunComplete, onNavConfigChange }) => {
  const sessionId = session?.uniqueId;

  // Remote data
  const [albumData, setAlbumData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // AI generation
  const [generating, setGenerating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const showSnackbar = (message, severity = 'info') =>
    setSnackbar({ open: true, message, severity });
  const [genError, setGenError] = useState(null);
  const [genElapsed, setGenElapsed] = useState(0); // seconds since generation started
  const genStartRef = React.useRef(null);
  const genTimerRef = React.useRef(null);

  // Story state
  const [story, setStory] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [userContext, setUserContext] = useState('');
  const [numberOfPages, setNumberOfPages] = useState(20);
  const [numberOfPagesInput, setNumberOfPagesInput] = useState('20');
  const [albumTopic, setAlbumTopic] = useState('');
  const [repImages, setRepImages] = useState(2);
  const [repImagesInput, setRepImagesInput] = useState('2');
  const [topImagesCount, setTopImagesCount] = useState(20);
  const [topImagesCountInput, setTopImagesCountInput] = useState('20');
  const [temperature, setTemperature] = useState(0.6);
  const [temperatureInput, setTemperatureInput] = useState('0.6');
  const [topImagesExpanded, setTopImagesExpanded] = useState(false);
  const [topImageDetailDialog, setTopImageDetailDialog] = useState({ open: false, imageId: null, selectionReasoning: null });
  const [reasoningOpen, setReasoningOpen] = useState(false);

  // Saving
  const [saving, setSaving] = useState(false);
  const [saveInfo, setSaveInfo] = useState(null);
  const [generatedSettingsSignature, setGeneratedSettingsSignature] = useState(null);

  // Image picker dialog
  const [pickerSceneIndex, setPickerSceneIndex] = useState(null);

  // Clear story confirmation
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  // Load album data + existing story on mount
  useEffect(() => {
    if (!sessionId) return;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [data, artifacts] = await Promise.all([
          albumService.getAlbumData(sessionId),
          albumService.getStoryTimeline(sessionId).catch(() => null),
        ]);
        setAlbumData(data);

        const defaults = computeDefaultStorySettings(data);
        const saved = artifacts?.storyTimeline;
        const savedSettings = saved?.storySettings || {};
        const sessionTopic = data.sessionEventTypes?.length > 0
          ? data.sessionEventTypes.join(', ')
          : (data.sessionEventType || '');
        
        // Use saved values if available, fallback to previousTargets (if story exists), then to computed defaults
        const resolvedSettings = {
          userContext: String(savedSettings.userContext || ''),
          albumTopic: String(savedSettings.albumTopic || sessionTopic || '').trim(),
          numberOfPages: savedSettings.numberOfPages 
            ? Math.max(1, parseInt(savedSettings.numberOfPages, 10))
            : (saved?.targetPages || defaults.numberOfPages),
          repImages: savedSettings.repImages
            ? Math.max(1, parseInt(savedSettings.repImages, 10))
            : (saved?.repImagesPerCluster || defaults.repImages),
          topImagesCount: savedSettings.topImagesCount
            ? Math.max(1, parseInt(savedSettings.topImagesCount, 10))
            : (saved?.topImages?.length || defaults.topImagesCount),
        };

        setUserContext(resolvedSettings.userContext);
        setAlbumTopic(resolvedSettings.albumTopic);
        setNumberOfPages(resolvedSettings.numberOfPages);
        setNumberOfPagesInput(String(resolvedSettings.numberOfPages));
        setRepImages(resolvedSettings.repImages);
        setRepImagesInput(String(resolvedSettings.repImages));
        setTopImagesCount(resolvedSettings.topImagesCount);
        setTopImagesCountInput(String(resolvedSettings.topImagesCount));

        if (saved) {
          setStory(saved);
          setScenes(
            (saved.scenes || []).map(s => ({
              ...s,
              imageIds: s.imageIds || s.suggestedImageIds || [],
              // Preserve saved includedInAlbum; default true if not stored
              includedInAlbum: s.includedInAlbum !== false,
            }))
          );
          setGeneratedSettingsSignature(makeSettingsSignature(resolvedSettings));
        } else {
          setGeneratedSettingsSignature(null);
        }
      } catch (err) {
        setLoadError(err.message || 'Failed to load album data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [sessionId]);

  const currentSettings = useMemo(() => sanitizeStorySettings({
    userContext,
    albumTopic,
    numberOfPages,
    repImages,
    topImagesCount,
    temperature,
  }), [userContext, albumTopic, numberOfPages, repImages, topImagesCount, temperature]);

  const currentSettingsSignature = useMemo(() => makeSettingsSignature(currentSettings), [currentSettings]);

  // Generate story — fire-and-forget POST, then poll until done
  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setGenError(null);
    setGenElapsed(0);
    genStartRef.current = Date.now();
    // Start elapsed-time ticker
    genTimerRef.current = setInterval(() => {
      setGenElapsed(Math.floor((Date.now() - genStartRef.current) / 1000));
    }, 1000);

    try {
      // Kick off — backend responds 202 immediately
      await albumService.generateStoryTimeline(sessionId, currentSettings);
      // Persist album topic back to session if it changed
      if (albumTopic && albumTopic !== (albumData?.sessionEventType || '')) {
        const types = albumTopic.split(',').map(t => t.trim()).filter(Boolean);
        sessionService.updateSession(sessionId, {
          settings: { eventTypes: types, eventType: types[0] || albumTopic }
        }).catch(() => {});
      }

      // Poll until done or failed
      const POLL_INTERVAL_MS = 3000;
      const MAX_WAIT_MS = 10 * 60 * 1000; // 10 minutes hard cap
      const pollStart = Date.now();
      while (Date.now() - pollStart < MAX_WAIT_MS) {
        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
        const prog = await albumService.getStoryProgress(sessionId);
        if (prog.status === 'done') {
          const s = prog.story;
          setStory(s);
          const tp = s?.targetPages || numberOfPages;
          const rawScenes = (s.scenes || []).map(sc => ({ ...sc, imageIds: sc.suggestedImageIds || [] }));
          const sortedByScore = [...rawScenes].sort((a, b) => (b.themeRelevanceScore || 0) - (a.themeRelevanceScore || 0));
          const topIds = new Set(sortedByScore.slice(0, tp).map(sc => sc.sceneId));
          setScenes(rawScenes.map(sc => ({ ...sc, includedInAlbum: topIds.has(sc.sceneId) })));
          setGeneratedSettingsSignature(currentSettingsSignature);
          setSaveInfo('Story regenerated with current settings.');
          showSnackbar(`✅ Story generation complete — ${rawScenes.length} scene(s) created.`, 'success');
          onRunComplete?.();
          return;
        }
        if (prog.status === 'failed') {
          setGenError(prog.error || 'Story generation failed on the server');
          return;
        }
        // status === 'in-progress' → keep polling
      }
      setGenError('Generation timed out — please try again');
    } catch (err) {
      setGenError(err.response?.data?.error || err.message || 'Failed to generate story timeline');
    } finally {
      clearInterval(genTimerRef.current);
      genTimerRef.current = null;
      setGenerating(false);
    }
  }, [sessionId, albumData, currentSettings, currentSettingsSignature, onRunComplete, numberOfPages]);

  // Clear story
  const handleClearStory = useCallback(async () => {
    setClearing(true);
    try {
      await albumService.clearStoryTimeline(sessionId);
      setStory(null);
      setScenes([]);
      setGeneratedSettingsSignature(null);
      setSaveInfo(null);
      setGenError(null);
    } catch (err) {
      setSaveInfo('Failed to clear story: ' + (err.response?.data?.error || err.message));
    } finally {
      setClearing(false);
      setClearConfirmOpen(false);
    }
  }, [sessionId]);

  // Toggle a scene's inclusion in the album
  const handleToggleInclude = useCallback((sceneId) => {
    setScenes(prev => prev.map(s => s.sceneId === sceneId ? { ...s, includedInAlbum: !s.includedInAlbum } : s));
  }, []);

  const hasStory = !!story && scenes.length > 0;
  const requiresRegeneration = hasStory && generatedSettingsSignature !== currentSettingsSignature;

  // Save edits
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const updated = story ? { ...story, scenes } : null;
      await albumService.saveStoryTimeline(sessionId, updated, currentSettings);
      if (updated) {
        setStory(updated);
      }
      setSaveInfo(updated ? 'Story edits and settings saved.' : 'Story settings saved.');
    } catch {
      setSaveInfo('Failed to save step settings.');
    }
    setSaving(false);
  }, [story, scenes, sessionId, currentSettings]);

  // Advance to generate step
  const handleNext = useCallback(async () => {
    if (requiresRegeneration) {
      setGenError('Settings changed after the last generation. Please regenerate the story before continuing.');
      return;
    }

    if (story) {
      const updated = { ...story, scenes };
      try { await albumService.saveStoryTimeline(sessionId, updated, currentSettings); } catch { /* ignore */ }
    }
    onNext({ storyTimeline: story ? { ...story, scenes } : null });
  }, [story, scenes, sessionId, onNext, requiresRegeneration, currentSettings]);

  // Context summary for the prompt info box
  const contextSummary = useMemo(() => {
    if (!albumData) return null;
    const clusterCount = (albumData.clusters || []).filter(c => c.id !== 'unclustered').length;
    const imageCount = albumData.images?.length || 0;
    const eventType = albumData.sessionEventTypes?.length > 0
      ? albumData.sessionEventTypes.join(', ')
      : (albumData.sessionEventType || 'general');
    return { clusterCount, imageCount, eventType };
  }, [albumData]);

  const allImages = useMemo(() => albumData?.images || [], [albumData]);

  // Largest cluster size — shown as a hint under the Rep. images field
  const largestClusterSize = useMemo(() => {
    const clusters = (albumData?.clusters || []).filter(c => c.id !== 'unclustered');
    return clusters.reduce((maxSize, cluster) => {
      if (Array.isArray(cluster.imageIds)) return Math.max(maxSize, cluster.imageIds.length);
      const count = (albumData?.images || []).filter(img => String(img.clusterId) === String(cluster.id)).length;
      return Math.max(maxSize, count);
    }, 0);
  }, [albumData]);

  // Dropdown options for number-of-scenes: smart fractions of clusterCount + common round numbers
  const sceneCountOptions = useMemo(() => {
    const clusterCount = (albumData?.clusters || []).filter(c => c.id !== 'unclustered').length;
    if (!clusterCount) return [5, 10, 15, 20];
    const fractions = [1/6, 1/4, 1/3, 1/2, 2/3, 3/4, 1].map(f => Math.max(1, Math.round(clusterCount * f)));
    const roundNums = [3, 5, 8, 10, 15, 20, 25, 30, 40, 50, 75, 100].filter(n => n <= Math.min(clusterCount, 200));
    return [...new Set([...roundNums, ...fractions])].sort((a, b) => a - b);
  }, [albumData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    onNavConfigChange?.({
      nextLabel: hasStory ? 'Next: Generate Album' : 'Continue without story',
      nextDisabled: requiresRegeneration,
      backDisabled: false,
      showSkip: !hasStory,
      skipLabel: 'Skip',
      onNextOverride: handleNext,
    });
  }, [hasStory, requiresRegeneration, handleNext]); // onNavConfigChange is stable

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">Loading album data…</Typography>
      </Box>
    );
  }

  if (loadError) {
    return <Alert severity="error">{loadError}</Alert>;
  }

  return (
    <Box>
      {/* ── Top navigation ──────────────────────────────────────────────────────── */}
      {/* ── Intro / context card ──────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <TimelineIcon color="primary" sx={{ mt: 0.25 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Story Builder
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              The AI will analyse your photo clusters and build a narrative timeline — chapters,
              days, locations, or event phases — that gives your album a clear story arc.
            </Typography>

            {contextSummary && (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={`${contextSummary.imageCount} photos`} size="small" variant="outlined" />
                <Chip label={`${contextSummary.clusterCount} clusters`} size="small" variant="outlined" color="primary" />
                <Chip label={contextSummary.eventType} size="small" variant="outlined" color="secondary" />
              </Stack>
            )}
          </Box>
        </Stack>
      </Paper>

      {/* ── User context input ────────────────────────────────────────────────── */}
      {!hasStory && (
        <TextField
          label="Add context for the album (optional)"
          placeholder="e.g. 'This is a 5-day family trip to Italy' or 'This is my daughter's wedding'"
          value={userContext}
          onChange={e => setUserContext(e.target.value)}
          multiline
          rows={2}
          fullWidth
          variant="outlined"
          size="small"
          sx={{ mb: 1.5 }}
          InputProps={{
            startAdornment: <InfoIcon color="action" sx={{ mr: 1, fontSize: 18, flexShrink: 0 }} />,
          }}
        />
      )}

      {/* ── Generation settings (always visible) ─────────────────────────────── */}
      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Album topic / theme"
          placeholder="e.g. Trip to Japan, Wedding day, Summer vacation"
          value={albumTopic}
          onChange={e => setAlbumTopic(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: 200 }}
          helperText="Used to rate each scene's relevance (1–10)"
        />
        <TextField
          label="Top images"
          type="number"
          value={topImagesCountInput}
          onChange={e => setTopImagesCountInput(e.target.value)}
          onBlur={() => {
            const v = Math.max(1, Math.min(200, parseInt(topImagesCountInput, 10) || 1));
            setTopImagesCount(v);
            setTopImagesCountInput(String(v));
          }}
          size="small"
          sx={{ width: 130 }}
          inputProps={{ min: 1, max: 200 }}
          helperText="Defaults to 20% of clusters"
        />
        <TextField
          label="Rep. images / cluster"
          type="number"
          value={repImagesInput}
          onChange={e => setRepImagesInput(e.target.value)}
          onBlur={() => {
            const v = Math.max(1, Math.min(200, parseInt(repImagesInput, 10) || 1));
            setRepImages(v);
            setRepImagesInput(String(v));
          }}
          size="small"
          sx={{ width: 160 }}
          inputProps={{ min: 1, max: 200 }}
          helperText={largestClusterSize > 0 ? `Biggest cluster: ${largestClusterSize} images` : 'Defaults to largest cluster size'}
        />
        <TextField
          label="Temperature"
          type="number"
          value={temperatureInput}
          onChange={e => setTemperatureInput(e.target.value)}
          onBlur={() => {
            const v = Math.max(0, Math.min(1, parseFloat(temperatureInput) || 0.6));
            setTemperature(v);
            setTemperatureInput(String(v));
          }}
          size="small"
          sx={{ width: 130 }}
          inputProps={{ min: 0, max: 1, step: 0.05 }}
          helperText="AI creativity (default: 0.6)"
        />
        <FormControl size="small" sx={{ width: 210 }}>
          <InputLabel id="num-scenes-label">
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PagesIcon sx={{ fontSize: 16 }} /> Number of scenes
            </Box>
          </InputLabel>
          <Select
            labelId="num-scenes-label"
            label="  Number of scenes"
            value={sceneCountOptions.includes(numberOfPages) ? numberOfPages : (sceneCountOptions[0] || numberOfPages)}
            onChange={e => {
              setNumberOfPages(Number(e.target.value));
              setNumberOfPagesInput(String(e.target.value));
            }}
          >
            {sceneCountOptions.map(n => (
              <MenuItem key={n} value={n}>{n}</MenuItem>
            ))}
            {/* Always include the currently set value if it's not in the computed options */}
            {!sceneCountOptions.includes(numberOfPages) && (
              <MenuItem value={numberOfPages}>{numberOfPages}</MenuItem>
            )}
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
            {hasStory ? `Currently ${scenes.length} scenes` : `Default: ⅓ of clusters`}
          </Typography>
        </FormControl>
        {hasStory && (
          <TextField
            label="Add context for the album (optional)"
            placeholder="Refine the story…"
            value={userContext}
            onChange={e => setUserContext(e.target.value)}
            size="small"
            variant="outlined"
            sx={{ flex: 1, minWidth: 220 }}
            helperText="Used when you click Regenerate Story. It gives the AI extra instructions (theme, people, moments, style)."
          />
        )}
      </Stack>

      {/* ── Generate button ───────────────────────────────────────────────────── */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoIcon />}
          onClick={handleGenerate}
          disabled={generating}
          size="large"
        >
          {generating
            ? `Generating story… ${genElapsed > 0 ? `(${genElapsed}s)` : ''}`
            : hasStory ? 'Regenerate Story' : 'Generate Story'}
        </Button>

        {hasStory && (
          <Button
            variant="outlined"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            onClick={handleSave}
            disabled={saving}
            size="small"
          >
            {saving ? 'Saving…' : 'Save story + settings (no regenerate)'}
          </Button>
        )}
        {!hasStory && (
          <Button
            variant="outlined"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            onClick={handleSave}
            disabled={saving}
            size="small"
          >
            {saving ? 'Saving…' : 'Save settings'}
          </Button>
        )}
        {hasStory && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setClearConfirmOpen(true)}
            disabled={generating || clearing}
            size="small"
          >
            Clear story
          </Button>
        )}
      </Stack>

      {/* ── Clear story confirm dialog ─────────────────────────────────── */}
      <Dialog open={clearConfirmOpen} onClose={() => setClearConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Clear story?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently delete the generated story from the database. Your settings will be kept.
            You can always regenerate a new story afterwards.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearConfirmOpen(false)} disabled={clearing}>Cancel</Button>
          <Button
            onClick={handleClearStory}
            color="error"
            variant="contained"
            disabled={clearing}
            startIcon={clearing ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
          >
            {clearing ? 'Clearing…' : 'Clear story'}
          </Button>
        </DialogActions>
      </Dialog>

      {saveInfo && (
        <Alert
          severity={saveInfo.toLowerCase().includes('failed') ? 'error' : 'success'}
          sx={{ mb: 2 }}
          onClose={() => setSaveInfo(null)}
        >
          {saveInfo}
        </Alert>
      )}

      {requiresRegeneration && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Story settings changed. Regenerate to apply these settings before continuing. Saving keeps the values but does not re-run AI.
        </Alert>
      )}

      {genError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGenError(null)}>
          {genError}
        </Alert>
      )}

      {/* ── Token / cost info ────────────────────────────────────────────── */}
      {story?.tokenStats && (() => {
        const ts = story.tokenStats;
        const a = ts.actual;
        const e = ts.estimated;
        const pct = a?.contextUsedPct ?? e.contextUsedPct;
        const pctColor = pct > 80 ? 'error' : pct > 50 ? 'warning' : 'default';
        return (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}>
              AI Generation Cost {a ? '(actual)' : '(estimated)'}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              <Chip label={ts.model} size="small" variant="outlined" />
              {ts.generatedAt && (
                <Chip
                  label={new Date(ts.generatedAt).toLocaleString('en-GB')}
                  size="small" variant="outlined"
                  title="Date and time of generation"
                />
              )}
              {ts.durationMs != null && (
                <Chip
                  label={`${(ts.durationMs / 1000).toFixed(1)}s`}
                  size="small" variant="outlined" color="info"
                  title="Model response time"
                />
              )}
              <Chip
                label={`${(a?.totalTokens ?? e.totalTokens).toLocaleString()} tokens`}
                size="small" color="info" variant="outlined"
                title={`Prompt: ${(a?.promptTokens ?? e.promptTokens).toLocaleString()} | Completion: ${(a?.completionTokens ?? e.completionTokens).toLocaleString()}`}
              />
              {(() => {
                const tokens = a ?? e;
                const isEst = !a;
                const inputCost = ((tokens.promptTokens * ts.priceInputPer1M) / 1_000_000).toFixed(4);
                const outputCost = ((tokens.completionTokens * ts.priceOutputPer1M) / 1_000_000).toFixed(4);
                const tag = isEst ? ' est.' : ' actual';
                return (
                  <>
                    <Chip
                      label={`in: $${inputCost}${tag}`}
                      size="small" variant="outlined"
                      title={`Input: ${tokens.promptTokens.toLocaleString()} tokens × $${ts.priceInputPer1M}/1M`}
                      sx={isEst ? { opacity: 0.75, fontStyle: 'italic' } : {}}
                    />
                    <Chip
                      label={`out: $${outputCost}${tag}`}
                      size="small" variant="outlined"
                      title={`Output: ${tokens.completionTokens.toLocaleString()} tokens × $${ts.priceOutputPer1M}/1M`}
                      sx={isEst ? { opacity: 0.75, fontStyle: 'italic' } : {}}
                    />
                  </>
                );
              })()}
              <Chip
                label={`Context: ${pct}%`}
                size="small" color={pctColor} variant="outlined"
                title={`Context limit: ${ts.contextLimit.toLocaleString()} tokens`}
              />
            </Stack>
          </Paper>
        );
      })()}

      {/* ── Generated story ─────────────────────────────────────────────────── */}
      {hasStory && (
        <>
          {/* Story header */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2.5, bgcolor: 'primary.50' }}>
            <Stack direction="row" alignItems="flex-start" spacing={1.5} flexWrap="wrap">
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  {story.storyTitle || 'Your Story'}
                </Typography>
                {story.storyDescription && (
                  <Typography variant="body2" color="text.secondary">
                    {story.storyDescription}
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {story.albumType && (
                  <Chip label={story.albumType} size="small" color="primary" />
                )}
                {story.organizationBy && (
                  <Chip
                    label={ORG_LABEL[story.organizationBy] || story.organizationBy}
                    size="small"
                    variant="outlined"
                  />
                )}
                <Chip label={`${scenes.length} generated`} size="small" color="success" variant="outlined" />
                {(() => {
                  const includedCount = scenes.filter(s => s.includedInAlbum !== false).length;
                  const target = story?.targetPages || numberOfPages;
                  return (
                    <Chip
                      label={`${includedCount} / ${target} for album`}
                      size="small"
                      color={includedCount === target ? 'success' : 'warning'}
                    />
                  );
                })()}
              </Stack>
            </Stack>
            {/* Debug reasoning panel */}
            {story.storyReasoning && (
              <Box sx={{ mt: 1.5, borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  onClick={() => setReasoningOpen(o => !o)}
                  sx={{ cursor: 'pointer', color: 'text.secondary', userSelect: 'none', width: 'fit-content' }}
                >
                  {reasoningOpen ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                  <Typography variant="caption" fontWeight={600}>Debug: AI Reasoning</Typography>
                </Stack>
                <Collapse in={reasoningOpen} timeout="auto">
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {story.storyReasoning.organizationRationale && (
                      <Box>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">Organization logic</Typography>
                        <Typography variant="caption" color="text.secondary">{story.storyReasoning.organizationRationale}</Typography>
                      </Box>
                    )}
                    {story.storyReasoning.topImagesSelectionNotes && (
                      <Box>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">Top images selection</Typography>
                        <Typography variant="caption" color="text.secondary">{story.storyReasoning.topImagesSelectionNotes}</Typography>
                      </Box>
                    )}
                    {story.storyReasoning.sceneRatingNotes && (
                      <Box>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">Scene scoring criteria</Typography>
                        <Typography variant="caption" color="text.secondary">{story.storyReasoning.sceneRatingNotes}</Typography>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Box>
            )}
          </Paper>

          {/* User context input when story exists - now moved above generate button */}

          {/* ── Top Images / Album Highlights ────────────────────────────── */}
          {Array.isArray(story.topImages) && story.topImages.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2.5, bgcolor: 'warning.50' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: topImagesExpanded || story.topImages.length <= 10 ? 1.5 : 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <StarIcon sx={{ color: 'warning.main', fontSize: 18 }} />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Album Highlights — Top {story.topImages.length} Images
                  </Typography>
                </Stack>
                {story.topImages.length > 10 && (
                  <Button size="small" onClick={() => setTopImagesExpanded(x => !x)} endIcon={<ExpandMoreIcon sx={{ transform: topImagesExpanded ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}>
                    {topImagesExpanded ? 'Show less' : `Show all ${story.topImages.length}`}
                  </Button>
                )}
              </Stack>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(topImagesExpanded ? story.topImages : story.topImages.slice(0, 10)).map((img, i) => (
                  <Tooltip key={img.imageId || i} title={[img.reason, img.selectionReasoning, img.sceneId ? `Scene: ${img.sceneId}` : ''].filter(Boolean).join(' — ')} placement="top" arrow>
                    <Box
                      onClick={() => setTopImageDetailDialog({ open: true, imageId: img.imageId, selectionReasoning: img.selectionReasoning || null })}
                      sx={{ position: 'relative', width: 90, height: 90, borderRadius: 1, overflow: 'hidden', flexShrink: 0, border: '1px solid', borderColor: 'warning.light', cursor: 'pointer', '&:hover': { opacity: 0.85, outline: '2px solid', outlineColor: 'warning.main' } }}
                    >
                      <AuthenticatedImage
                        imageId={img.imageId}
                        type="thumbnail"
                        fallbackToServe
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      {/* Score badge */}
                      <Box sx={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        bgcolor: 'rgba(0,0,0,0.55)', px: 0.5, py: 0.25,
                        display: 'flex', alignItems: 'center', gap: 0.25,
                      }}>
                        <StarIcon sx={{ color: 'warning.light', fontSize: 11 }} />
                        <Typography variant="caption" sx={{ color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: 1 }}>
                          {img.imageImportanceScore}/10
                        </Typography>
                      </Box>
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Paper>
          )}

          {/* Scene cards */}
          <Stack spacing={1.5} sx={{ mb: 3 }}>
            {scenes.map((scene, idx) => (
              <SceneCard
                key={scene.sceneId || idx}
                scene={scene}
                sceneIndex={idx}
                allImages={allImages}
                onEditImages={setPickerSceneIndex}
                included={scene.includedInAlbum !== false}
                onToggleInclude={handleToggleInclude}
                sceneRationale={story?.storyReasoning?.sceneRatingNotes}
              />
            ))}
          </Stack>
        </>
      )}

      {/* ── Image picker dialog ───────────────────────────────────────────────── */}
      {pickerSceneIndex !== null && (
        <ImagePickerDialog
          open
          sceneIndex={pickerSceneIndex}
          sceneName={scenes[pickerSceneIndex]?.sceneName || ''}
          scenes={scenes}
          setScenes={setScenes}
          allImages={allImages}
          onClose={() => setPickerSceneIndex(null)}
        />
      )}

      {/* ── Navigation ────────────────────────────────────────────────────────── */}
      <Divider sx={{ my: 3 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack}>
          Back to Clustering
        </Button>

        <Stack direction="row" spacing={1}>
          {!hasStory && (
            <Tooltip title="Skip this step and go straight to Generate Album">
              <Button
                variant="text"
                endIcon={<SkipIcon />}
                onClick={() => onNext({ storyTimeline: null })}
                color="inherit"
              >
                Skip
              </Button>
            </Tooltip>
          )}
          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            onClick={handleNext}
            color="primary"
            disabled={requiresRegeneration}
          >
            {hasStory ? 'Next: Generate Album' : 'Continue without story'}
          </Button>
        </Stack>
      </Stack>
      {/* ── Top image detail dialog ────────────────────────────────────────── */}
      <ImageAnalysisDetailsDialog
        open={topImageDetailDialog.open}
        onClose={() => setTopImageDetailDialog({ open: false, imageId: null, selectionReasoning: null })}
        imageId={topImageDetailDialog.imageId}
        selectionReasoning={topImageDetailDialog.selectionReasoning}
      />

      {/* ── Snackbar ──────────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WizardStep5Story;
